import type { ReactNode } from 'react'
import { cn } from '../cn.js'

export type AlertTone = 'info' | 'success' | 'error'

export interface AlertProps {
  tone?: AlertTone
  title?: string
  children: ReactNode
  className?: string
}

/** Colour is never the only signal — each tone carries an icon and a label. */
const TONES: Record<AlertTone, { classes: string; icon: string; label: string }> = {
  info: { classes: 'border-line-strong bg-surface-alt text-ink', icon: 'ℹ', label: 'Information' },
  success: { classes: 'border-success bg-white text-ink', icon: '✓', label: 'Success' },
  error: { classes: 'border-danger bg-danger-surface text-ink', icon: '⚠', label: 'Error' },
}

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  const t = TONES[tone]
  return (
    <div
      // Errors interrupt; informational messages wait their turn.
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-lg border p-4', t.classes, className)}
    >
      <span aria-hidden="true" className="shrink-0 text-lg leading-none">
        {t.icon}
      </span>
      <span className="sr-only">{t.label}:</span>
      <div className="min-w-0 text-sm">
        {title && <p className="font-semibold">{title}</p>}
        <div className={cn(title && 'mt-0.5')}>{children}</div>
      </div>
    </div>
  )
}
